"use client";

import React from "react";
import {
  OakBox,
  OakCheckBox,
  OakFlex,
  OakHeading,
  OakLabel,
  OakLink,
  OakMaxWidth,
  OakP,
} from "@oaknational/oak-components";

import { GoogleSignInButton, OakAcornIcon } from "@/components";
import { AuthCookieKeys } from "@/types/ui";

type Props = {
  getGoogleSignInLink: (
    subscribeToNewsletter?: boolean,
  ) => Promise<string | null>;
  onSuccessfulSignIn: (signUpToNewsletter: boolean) => Promise<void> | void;
  privacyPolicyUrl: string;
  showMailingListOption?: boolean;
  cookieKeys?: [
    AuthCookieKeys.AccessToken | AuthCookieKeys.PupilAccessToken,
    AuthCookieKeys.Session | AuthCookieKeys.PupilSession,
  ];
};
export const GoogleSignInView = ({
  getGoogleSignInLink,
  onSuccessfulSignIn,
  privacyPolicyUrl,
  showMailingListOption = true,
  cookieKeys = [AuthCookieKeys.AccessToken, AuthCookieKeys.Session],
}: Props) => {
  const popupRef = React.useRef<Window | null>(null);
  const [isNewsletterChecked, setIsNewsletterChecked] = React.useState(false);
  const [accessTokenCookieKey, sessionCookieKey] = cookieKeys;

  const onSignInClick = async () => {
    const signInLink = await getGoogleSignInLink(isNewsletterChecked);
    if (!signInLink) throw new Error("Could not get Google Sign In link");
    const validLinkRegex = new RegExp(/^(https):/);
    if (validLinkRegex.test(signInLink)) {
      popupRef.current = window.open(
        signInLink,
        "popUpWindow",
        "height=500,width=500,left=100,top=100,resizable=no," +
          "scrollbars=yes,toolbar=yes,menubar=no,location=yes," +
          "directories=no,status=yes",
      );
    } else {
      // todo: show error toast
      console.error("getGoogleSignInLink did not return a valid URL");
    }
  };
  const handleAuthMessage = React.useCallback(
    (event: MessageEvent) => {
      if (
        event.data &&
        event.data.type === "oak-google-classroom-auth-complete"
      ) {
        if (popupRef.current) {
          popupRef.current.close();
          popupRef.current = null;
        }
        if (
          event.data.success &&
          event.data.session &&
          event.data.accessToken
        ) {
          window?.cookieStore?.set({
            name: sessionCookieKey,
            value: event.data.session,
            partitioned: true,
            sameSite: "none",
            path: "/",
          });
          window?.cookieStore?.set({
            name: accessTokenCookieKey,
            value: event.data.accessToken,
            partitioned: true,
            sameSite: "none",
            path: "/",
          });
          onSuccessfulSignIn(isNewsletterChecked);
        }
      }
    },
    [
      onSuccessfulSignIn,
      isNewsletterChecked,
      accessTokenCookieKey,
      sessionCookieKey,
    ],
  );

  React.useEffect(() => {
    window.addEventListener("message", handleAuthMessage);
    return () => {
      window.removeEventListener("message", handleAuthMessage);
    };
  }, [handleAuthMessage]);

  return (
    <OakMaxWidth $pa={"spacing-40"} $maxWidth={"spacing-480"} $ma={"auto"}>
      <OakFlex $flexDirection="column" $rowGap={"spacing-24"}>
        <OakAcornIcon />
        <OakHeading tag={"h1"} $font={"heading-6"}>
          Sign in to Oak National Academy
        </OakHeading>
        <OakP $font={"body-2"}>Welcome back! Please sign-in to continue</OakP>
        <GoogleSignInButton onClick={onSignInClick} />
        {showMailingListOption && (
          <OakFlex $columnGap={"spacing-12"} $alignItems={"flex-start"}>
            <OakCheckBox
              id={"google-sign-in-newsletter-checkbox"}
              value=""
              checked={isNewsletterChecked}
              onChange={(e) => setIsNewsletterChecked(e.target.checked)}
            />
            <OakBox>
              <OakLabel
                $font={"body-2"}
                htmlFor={"google-sign-in-newsletter-checkbox"}
              >
                Sign up to receive helpful content via email. Unsubscribe at any
                time. Read our{" "}
              </OakLabel>
              <OakLink
                href={privacyPolicyUrl}
                isTrailingIcon={true}
                iconName={"external"}
                target={"_blank"}
              >
                privacy policy
              </OakLink>
              .
            </OakBox>
          </OakFlex>
        )}
      </OakFlex>
    </OakMaxWidth>
  );
};
